import React, { useState } from 'react';
import { useAppContext } from '../App';

const SearchSection = () => {
  const { handleSearch, showNotification } = useAppContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    purity: ''
  });

  const onSearch = (e) => {
    e.preventDefault();
    handleSearch(searchQuery, filters);
    if (searchQuery || filters.type || filters.purity) {
      showNotification(`Searching for "${searchQuery || 'all chemicals'}"...`, 'info');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilters({ type: '', purity: '' });
    handleSearch('', { type: '', purity: '' });
  };

  const hasActiveFilters = searchQuery || filters.type || filters.purity;

  return (
    <section className="search-section animate-fade-in-up delay-1" id="home">
      <div className="container">
        <div className="search-hero">
          <h1 className="search-title">
            Global Chemical <span className="gradient-text">Marketplace</span>
          </h1>
          <p className="search-subtitle">
            Connect with verified suppliers, access real-time market data, and streamline your chemical sourcing
          </p>
        </div>

        <form onSubmit={onSearch} className="search-form">
          <div className="search-input-wrapper">
            <div className="search-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </div>
            <input
              type="text"
              className="search-input"
              placeholder="Search chemicals by CAS#, name, application, or supplier..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="clear-btn"
                onClick={() => setSearchQuery('')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
            <button type="submit" className="search-btn">
              Search
            </button>
          </div>

          <div className="search-filters">
            <select
              className="filter-select"
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            >
              <option value="">All Types</option>
              <option value="organic">Organic</option>
              <option value="inorganic">Inorganic</option>
              <option value="solvent">Solvent</option>
              <option value="surfactant">Surfactant</option>
            </select>

            <select
              className="filter-select"
              value={filters.purity}
              onChange={(e) => setFilters({ ...filters, purity: e.target.value })}
            >
              <option value="">Any Purity</option>
              <option value="95">≥95%</option>
              <option value="99">≥99%</option>
              <option value="99.5">≥99.5%</option>
              <option value="99.9">≥99.9%</option>
            </select>

            {hasActiveFilters && (
              <button type="button" className="clear-filters-btn" onClick={clearFilters}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Clear
              </button>
            )}
          </div>
        </form>

        <div className="search-stats">
          <div className="stat-item">
            <span className="stat-number">50,000+</span>
            <span className="stat-label">Chemicals</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-number">2,500+</span>
            <span className="stat-label">Verified Suppliers</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <span className="stat-number">120+</span>
            <span className="stat-label">Countries</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        .search-section {
          padding: var(--space-3xl) 0 var(--space-2xl);
          background: linear-gradient(180deg, rgba(59, 130, 246, 0.08) 0%, transparent 100%);
          border-bottom: 1px solid rgba(59, 130, 246, 0.1);
        }

        .search-hero {
          text-align: center;
          margin-bottom: var(--space-2xl);
        }

        .search-title {
          font-size: var(--font-5xl);
          font-weight: 700;
          color: var(--color-neutral-50);
          margin-bottom: var(--space-md);
          line-height: 1.1;
        }

        .gradient-text {
          background: linear-gradient(135deg, var(--color-accent-400), var(--color-accent-300));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .search-subtitle {
          font-size: var(--font-lg);
          color: var(--color-neutral-400);
          max-width: 700px;
          margin: 0 auto;
        }

        .search-form {
          max-width: 900px;
          margin: 0 auto var(--space-xl);
        }

        .search-input-wrapper {
          display: flex;
          align-items: center;
          background: rgba(255, 255, 255, 0.05);
          border: 2px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-xl);
          padding: var(--space-sm);
          margin-bottom: var(--space-md);
          transition: all var(--transition-base);
        }

        .search-input-wrapper:focus-within {
          border-color: var(--color-accent-400);
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15);
          background: rgba(255, 255, 255, 0.08);
        }

        .search-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          color: var(--color-neutral-400);
        }

        .search-input {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          font-family: var(--font-body);
          font-size: var(--font-base);
          color: var(--color-neutral-50);
          padding: var(--space-sm);
        }

        .search-input::placeholder {
          color: var(--color-neutral-500);
        }

        .clear-btn {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          border-radius: 50%;
          color: var(--color-neutral-400);
          cursor: pointer;
          margin-right: var(--space-sm);
          transition: all var(--transition-fast);
        }

        .clear-btn:hover {
          background: rgba(239, 68, 68, 0.3);
          color: #ef4444;
        }

        .search-btn {
          font-family: var(--font-display);
          font-weight: 600;
          font-size: var(--font-sm);
          padding: var(--space-sm) var(--space-xl);
          background: linear-gradient(135deg, var(--color-accent-500), var(--color-accent-600));
          color: white;
          border: none;
          border-radius: var(--radius-lg);
          cursor: pointer;
          transition: all var(--transition-fast);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .search-btn:hover {
          background: linear-gradient(135deg, var(--color-accent-400), var(--color-accent-500));
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        .search-filters {
          display: flex;
          gap: var(--space-md);
          justify-content: center;
          flex-wrap: wrap;
        }

        .filter-select {
          font-family: var(--font-body);
          font-size: var(--font-sm);
          padding: var(--space-sm) var(--space-lg);
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: var(--radius-md);
          color: var(--color-neutral-200);
          cursor: pointer;
          transition: all var(--transition-fast);
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          padding-right: 40px;
        }

        .filter-select:hover {
          background-color: rgba(255, 255, 255, 0.08);
          border-color: var(--color-accent-400);
        }

        .filter-select:focus {
          outline: none;
          border-color: var(--color-accent-400);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }

        .clear-filters-btn {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
          font-family: var(--font-body);
          font-size: var(--font-sm);
          padding: var(--space-sm) var(--space-md);
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: var(--radius-md);
          color: #ef4444;
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .clear-filters-btn:hover {
          background: rgba(239, 68, 68, 0.2);
        }

        .search-stats {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--space-xl);
          padding-top: var(--space-xl);
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--space-xs);
        }

        .stat-number {
          font-family: var(--font-display);
          font-size: var(--font-3xl);
          font-weight: 700;
          background: linear-gradient(135deg, var(--color-accent-400), var(--color-accent-300));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .stat-label {
          font-size: var(--font-sm);
          color: var(--color-neutral-400);
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .stat-divider {
          width: 1px;
          height: 40px;
          background: rgba(255, 255, 255, 0.1);
        }

        @media (max-width: 768px) {
          .search-title {
            font-size: var(--font-4xl);
          }

          .search-filters {
            flex-direction: column;
            align-items: stretch;
          }

          .filter-select {
            width: 100%;
          }

          .search-stats {
            flex-direction: column;
            gap: var(--space-md);
          }

          .stat-divider {
            width: 60px;
            height: 1px;
          }

          .search-input-wrapper {
            flex-wrap: wrap;
          }

          .search-btn {
            width: 100%;
            margin-top: var(--space-sm);
          }
        }
      `}</style>
    </section>
  );
};

export default SearchSection;
